import { getCornerstone } from "../utils/dicomLoader";

/** The BaseStreamingImageVolume surface the priority request drives. */
interface StreamingVolume {
  loadStatus: { loaded: boolean };
  cachedFrames: number[];
  getImageIdIndex: (imageId: string) => number;
  getLoaderImageOptions: (imageId: string) => unknown;
  callLoadImage: (
    imageId: string,
    imageIdIndex: number,
    options: unknown,
  ) => Promise<void> | undefined;
}

/**
 * Fetch the frame the viewer is showing ahead of the background stream.
 *
 * The streaming volume fills frames in interleaved stages (middle, ends, then
 * every 4th). A pane that opens or scrolls onto a frame no stage has reached yet
 * shows the zero-filled texture, which reads as flat gray under a soft-tissue
 * window. Queuing the volume's own load for that frame as an Interaction
 * request puts it ahead of the Thumbnail-priority stages. The volume ignores a
 * frame that is already at full resolution, so the stage's later delivery of
 * the same frame is a no-op.
 */
export function prioritizeVolumeFrame(volumeId: string, imageId: string): void {
  const cornerstone = getCornerstone();
  const volume = cornerstone.cache.getVolume(volumeId) as unknown as StreamingVolume | undefined;
  if (!volume || volume.loadStatus.loaded) return;
  const imageIdIndex = volume.getImageIdIndex(imageId);
  if (imageIdIndex < 0) return;
  const { ImageQualityStatus, RequestType } = cornerstone.Enums;
  if (volume.cachedFrames[imageIdIndex] === ImageQualityStatus.FULL_RESOLUTION) return;
  const options = volume.getLoaderImageOptions(imageId);
  cornerstone.imageLoadPoolManager.addRequest(
    async () => {
      await volume.callLoadImage(imageId, imageIdIndex, options);
    },
    RequestType.Interaction,
    { imageId, volumeId },
    0,
  );
}
